<template>
    <div class="page">
        <div class="container">
            <div class="head">
                <div>
                    <div class="title">Devices List</div>
                    <div class="sub">Manage proxies, monitor bandwidth, and track device status.</div>
                </div>
                <div class="actions">
                    <el-button size="small" @click="onExport">
                        <i class="el-icon-download" />
                        <span>Export</span>
                    </el-button>
                </div>
            </div>

            <div class="filter-card">
                <div class="filter-row">
                    <el-input v-model="filters.keyword" size="small" clearable
                        placeholder="Filter by Device ID or Username" class="keyword" @keyup.enter.native="onApply" />

                    <div class="filter-right">
                        <el-select v-model="filters.status" size="small" clearable placeholder="Status: All"
                            class="pill" @change="onApply">
                            <el-option label="Active" value="active" />
                            <el-option label="Inactive" value="inactive" />
                        </el-select>

                        <el-select v-model="filters.country" size="small" clearable placeholder="Country: All"
                            class="pill" filterable @change="onApply">
                            <el-option v-for="c in countries" :key="c.code" :label="c.name" :value="c.code.toLowerCase()" />
                        </el-select>



                        <el-button size="small" type="primary" @click="onApply">
                            Filter
                        </el-button>

                        <el-button type="text" class="reset" @click="onReset">Reset</el-button>
                    </div>
                </div>
            </div>

            <div class="table-card">
                <el-table ref="table" v-loading="loading" :data="rows" :height="tableHeight" stripe
                    class="devices-table" style="width: 100%">
                    <el-table-column label="Device ID" min-width="160">
                        <template slot-scope="scope">
                            <div class="device-id">
                                <span class="mono strong">{{ scope.row.email || scope.row.id }}</span>
                                <el-button type="text" class="icon-btn" @click="copyText(scope.row.email || scope.row.id)">
                                    <i class="el-icon-document-copy" />
                                </el-button>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="IP Address" min-width="170">
                        <template slot-scope="scope">
                            <div class="ip-cell">
                                <span class="mono">{{ scope.row.ip || '-' }}</span>
                                <el-button v-if="scope.row.ip" type="text" class="icon-btn"
                                    @click="copyText(scope.row.ip)">
                                    <i class="el-icon-document-copy" />
                                </el-button>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="Country" min-width="140">
                        <template slot-scope="scope">
                            <div class="country-cell">
                                <img v-if="scope.row.country" class="flag" :src="flagUrl(scope.row.country)"
                                    :alt="scope.row.country" />
                                <span class="mono">{{ scope.row.country || '-' }}</span>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="License" min-width="220">
                        <template slot-scope="scope">
                            <div class="cred">
                                <div class="cred-pass">
                                    <code
                                        class="code">{{ isKeyVisible(scope.row) ? (scope.row.license || '') : '•••••••' }}</code>
                                    <el-button type="text" class="icon-btn" @click="toggleKey(scope.row)">
                                        <i class="el-icon-view" />
                                    </el-button>
                                </div>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="Sessions" min-width="190">
                        <template slot-scope="scope">
                            <div class="bw-now">
                                <div class="bw-line">
                                    <span class="mono strong" style="color:#22c55e">{{ scope.row.sessions_active }}</span>
                                    <span class="muted">active</span>
                                </div>
                                <div class="bw-line muted">
                                    <span class="mono">{{ scope.row.sessions_inactive }}</span>
                                    <span class="muted">inactive</span>
                                </div>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="Bandwidth" min-width="220">
                        <template slot-scope="scope">
                            <div class="bw-now">
                                <div class="bw-line">
                                    <i class="el-icon-download" style="color:#22c55e" />
                                    <span class="mono strong">{{ fmtBytes(scope.row.in) }}</span>
                                </div>
                                <div class="bw-line muted">
                                    <i class="el-icon-upload2" style="color:#3b82f6" />
                                    <span class="mono">{{ fmtBytes(scope.row.out) }}</span>
                                </div>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="Created At" min-width="170">
                        <template slot-scope="scope">
                            <span class="mono muted">{{ fmtDateTime(scope.row.created_at) }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="Status" width="140" align="center">
                        <template slot-scope="scope">
                            <span class="status-pill" :class="statusClass(scope.row)">
                                <span class="dot" />
                                {{ statusLabel(scope.row) }}
                            </span>
                        </template>
                    </el-table-column>

                    <el-table-column label="Actions" width="90" align="right">
                        <template slot-scope="scope">
                            <el-dropdown trigger="click">
                                <el-button type="text" class="more-btn">
                                    <i class="el-icon-more" />
                                </el-button>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item @click.native="copyText(scope.row.email || scope.row.id)">Copy ID</el-dropdown-item>
                                    <el-dropdown-item @click.native="copyText(scope.row.ip || '')">Copy
                                        IP</el-dropdown-item>
                                    <el-dropdown-item @click.native="copyText(scope.row.license || '')">Copy License</el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="footer">
                    <div class="footer-left">
                        Showing <span class="strong">{{ showingFrom }}</span> to <span class="strong">{{ showingTo
                            }}</span> of <span class="strong">{{ total }}</span> results
                    </div>
                    <div class="footer-right">
                        <el-pagination :current-page="currentPage" :page-size="limit" :page-sizes="[10, 25, 50]"
                            :total="total" layout="sizes, prev, pager, next" @size-change="onSizeChange"
                            @current-change="onCurrentChange" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getAdminUserNodes } from '../../api/peers'
import { COUNTRIES } from '../../constants/countries'

export default {
    name: 'DevicesView',
    data() {
        return {
            loading: false,
            rows: [],
            total: 0,
            limit: 25,
            page: 1,
            countries: COUNTRIES,
            tableHeight: 420,
            keyVisible: {},
            filters: {
                keyword: '',
                status: '',
                country: '',
                bandwidth: null,
                created_at: [],
                sort_by: ''
            }
        }
    },
    computed: {
        currentPage() {
            return this.page
        },
        showingFrom() {
            if (!this.total) return 0
            return (this.page - 1) * this.limit + 1
        },
        showingTo() {
            return Math.min(this.page * this.limit, this.total)
        }
    },
    mounted() {
        this.fetchDevices()
        this.$nextTick(this.calcTableHeight)
        window.addEventListener('resize', this.calcTableHeight, false)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calcTableHeight, false)
    },
    methods: {
        calcTableHeight() {
            try {
                const el = this.$refs.table && this.$refs.table.$el ? this.$refs.table.$el : null
                if (!el) return
                const rect = el.getBoundingClientRect()
                const footer = this.$el ? this.$el.querySelector('.footer') : null
                const footerH = footer ? footer.getBoundingClientRect().height : 0
                const paddingBottom = 24
                const h = Math.floor(window.innerHeight - rect.top - footerH - paddingBottom)
                this.tableHeight = Math.max(260, h)
            } catch (e) { }
        },
        onExport() {
            this.$message && this.$message.info('Export (demo)')
        },

        async copyText(text) {
            const t = String(text || '')
            if (!t) return
            try {
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(t)
                } else {
                    const el = document.createElement('textarea')
                    el.value = t
                    el.setAttribute('readonly', '')
                    el.style.position = 'absolute'
                    el.style.left = '-9999px'
                    document.body.appendChild(el)
                    el.select()
                    document.execCommand('copy')
                    document.body.removeChild(el)
                }
                this.$message && this.$message.success('Copied')
            } catch (e) {
                this.$message && this.$message.error('Copy failed')
            }
        },
        isKeyVisible(row) {
            const id = row && row.id
            return !!(id && this.keyVisible[id])
        },
        toggleKey(row) {
            const id = row && row.id
            if (!id) return
            this.$set(this.keyVisible, id, !this.keyVisible[id])
        },
        usedProxies() {
            return 0
        },
        proxyPercent(row) {
            const used = this.usedProxies(row)
            const max = Number(row && row.proxy_limit || 50) || 50
            return Math.max(0, Math.min(100, Math.round((used / max) * 100)))
        },
        fmtBytes(n) {
            const num = Number(n || 0)
            const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
            let v = num
            let i = 0
            while (v >= 1024 && i < units.length - 1) {
                v = v / 1024
                i++
            }
            return (i === 0 ? String(Math.round(v)) : v.toFixed(2)) + ' ' + units[i]
        },
        fmtRate(bytesPerSec) {
            return this.fmtBytes(bytesPerSec) + '/s'
        },
        flagUrl(code) {
            const c = String(code || '').trim().toLowerCase()
            if (!c) return ''
            return `https://flagcdn.com/24x18/${c}.png`
        },
        fmtDateTime(iso) {
            const s = String(iso || '')
            if (!s) return '-'
            const d = new Date(s)
            if (isNaN(d.getTime())) return s
            const pad = (n) => String(n).padStart(2, '0')
            const yyyy = d.getFullYear()
            const mm = pad(d.getMonth() + 1)
            const dd = pad(d.getDate())
            const hh = pad(d.getHours())
            const mi = pad(d.getMinutes())
            return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
        },
        statusLabel(row) {
            const s = (row && row.status) ? String(row.status) : ''
            if (s === 'active') return 'Active'
            if (s === 'inactive') return 'Inactive'
            return s || '—'
        },
        statusClass(row) {
            const s = (row && row.status) ? String(row.status) : ''
            if (s === 'active') return 'is-online'
            if (s === 'inactive') return 'is-offline'
            return ''
        },
        async fetchDevices() {
            this.loading = true
            try {
                const res = await getAdminUserNodes({
                    page: this.page,
                    size: this.limit,
                    keyword: this.filters.keyword,
                    status: this.filters.status,
                    country: this.filters.country
                })
                const d = res && res.data ? res.data : res
                this.rows = (d && d.items) ? d.items : []
                this.total = (d && typeof d.total === 'number') ? d.total : 0
            } catch (e) {
                this.$message && this.$message.error('Không tải được devices')
            } finally {
                this.loading = false
                this.$nextTick(this.calcTableHeight)
            }
        },
        onApply() {
            this.page = 1
            this.fetchDevices()
        },
        onReset() {
            this.filters = { keyword: '', status: '', country: '', bandwidth: null, created_at: [], sort_by: '' }
            this.page = 1
            this.fetchDevices()
        },
        onSizeChange(size) {
            this.limit = size
            this.page = 1
            this.fetchDevices()
        },
        onCurrentChange(page) {
            this.page = Number(page || 1)
            this.fetchDevices()
        }
    }
}
</script>

<style scoped>
.page {
    padding: 16px;
}

.container {
    max-width: 1600px;
    margin: 0 auto;
}

.head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.title {
    font-size: 28px;
    font-weight: 900;
    color: #ffffff;
}

.sub {
    margin-top: 6px;
    font-size: 12px;
    color: #94a3b8;
}

.actions {
    display: flex;
    gap: 10px;
}

.filter-card {
    background: #1a2235;
    border: 1px solid #232f48;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 14px;
    overflow-x: auto;
    /* giữ 1 dòng, thiếu chỗ thì scroll ngang */
}

.filter-row {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    min-width: 980px;
}

.keyword {
    min-width: 280px;
    flex: 1;
}

.filter-right {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
}

.pill {
    min-width: 160px;
}

.date-pill {
    min-width: 260px;
}

.reset {
    color: #135bec;
    font-weight: 700;
}

.filter-btn /deep/ i {
    margin-right: 6px;
}

.table-card {
    background: #1a2235;
    border: 1px solid #232f48;
    border-radius: 12px;
    overflow: hidden;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.strong {
    font-weight: 800;
}

.device-id {
    display: flex;
    align-items: center;
    gap: 8px;
}

.country-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.ip-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.flag {
    width: 20px;
    height: 14px;
    border-radius: 2px;
}

.icon-btn {
    padding: 0;
    min-height: 0;
    color: #9ca3af;
}

.icon-btn:hover {
    color: #135bec;
}

.cred {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cred-user {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #94a3b8;
}

.cred-pass {
    display: flex;
    align-items: center;
    gap: 8px;
}

.code {
    padding: 2px 6px;
    border-radius: 6px;
    background: #111722;
    border: 1px solid #324467;
    color: #ffffff;
    font-size: 12px;
    letter-spacing: 0.6px;
}

.proxy-usage {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: center;
}

.bw-now {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.bw-line {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.bw-sum {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.slash {
    color: #64748b;
}

.tiny {
    margin-top: 4px;
    font-size: 10px;
}

.muted {
    color: #92a4c9;
    font-size: 12px;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid transparent;
}

.dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: currentColor;
}

.status-pill.is-online {
    color: #34d399;
    background: rgba(34, 197, 94, 0.12);
    border-color: rgba(34, 197, 94, 0.22);
}

.status-pill.is-idle {
    color: #fbbf24;
    background: rgba(234, 179, 8, 0.12);
    border-color: rgba(234, 179, 8, 0.22);
}

.status-pill.is-offline {
    color: #f87171;
    background: rgba(239, 68, 68, 0.12);
    border-color: rgba(239, 68, 68, 0.22);
}

.more-btn {
    color: #94a3b8;
}

.more-btn:hover {
    color: #ffffff;
    background: #232f48;
}

.footer {
    padding: 10px 12px;
    border-top: 1px solid #2d313a;
    background: #161e2c;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.footer-left {
    color: #92a4c9;
    font-size: 12px;
}

.footer-right /deep/ .el-pager li {
    background: transparent;
    color: #92a4c9;
}

.footer-right /deep/ .el-pager li.active {
    color: #ffffff;
    background: #135bec;
    border-radius: 8px;
}

.footer-right /deep/ .btn-prev,
.footer-right /deep/ .btn-next {
    background: #232f48;
    color: #ffffff;
    border-radius: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/devices/index.vue